import { Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as LogoutIcon } from 'new/components/icons/fi-rr-sign-out-alt.svg';
import { logout, switchToOrg } from 'js/actions';
import { getCurrentUser } from 'js/reducers/session';
import styles from './userDropdown.module.scss';
import React, { useCallback, useState } from 'react';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { TableProfile } from 'new/elements/TableProfile';
import UIUtils from 'js/lib/UIUtils';
import { useHistory } from 'react-router-dom';
import { DialogSessionToken } from 'js/components/token/DialogSessionToken';
import UserPage from 'new/components/settings/userPage/UserPage';
import { ChipBadge } from 'new/elements/ChipBadge';
import OrganizationTrialStatus from 'src/new/components/OrganizationTrialStatus';
import { ProfileImage } from 'new/elements/ProfileImage';
import { useImpersonatedUser } from 'new/hooks/adapters/useImpersonatedUser';

const UserDropdown = () => {
  const currentUser = useSelector(getCurrentUser);
  const [showSessionTokenDialog, setShow] = useState(false);
  const currentOrg = useCurrentOrg();
  const otherOrgs = currentUser?.org_memberships;
  const session = useSelector((state: any) => state.session);
  const gProfile = session?.googleAuth?.profile;
  const dispatch = useDispatch();
  const history = useHistory();
  const impersonatedUser = useImpersonatedUser();

  const handleOrgSwitch = useCallback(
    (orgId) => () => {
      if (orgId === currentOrg.id) return;
      const redirectedPath = UIUtils.getMainPage();
      history.push(redirectedPath);
      dispatch(switchToOrg(orgId));
    },
    [currentOrg],
  );

  const onClickSessionToken = () => {
    setShow(true);
  };

  const onCloseSessionToken = () => {
    setShow(false);
  };

  const onClickAccount = () => {
    history.push(UserPage.screenConfig.path);
  };

  return (
    <div className={styles.dropdown} data-testid="user-dropdown">
      <div className={styles.avatarRow}>
        <ProfileImage
          className={styles.avatar}
          source={gProfile?.email === currentUser?.email ? gProfile?.getImageUrl() : undefined}
          fullName={currentUser?.full_name}
        />
      </div>
      <Typography variant="subtitle1" align="center">
        {currentUser.full_name}
      </Typography>
      <Typography variant="body3" align="center">
        {currentUser.email}
      </Typography>
      <Button
        onClick={onClickAccount}
        variant="outlined"
        size="large"
        className={styles.accountButton}
      >
        Account Preferences
      </Button>
      <Button
        onClick={onClickSessionToken}
        className={styles.sessionTokenButton}
        variant="text"
        color="primary"
      >
        Get Session Token
      </Button>

      <OrganizationTrialStatus type="usage" />

      <div className={styles.logout} onClick={() => dispatch(logout())} data-testid="logout-item">
        <Typography variant="caption">Log out of {currentOrg.name}</Typography>
        <LogoutIcon />
      </div>
      {!!otherOrgs?.length && (
        <div className={styles.accountSwitcher}>
          <Typography className={styles.switchOrgTitle}>Switch Active Org</Typography>
          <div aria-label="orgs-list-container">
            {otherOrgs.map((org: { id: string; name: string }) => {
              const isActived = org.id === currentOrg.id;
              const isDisabled = impersonatedUser && !isActived;
              return (
                <button
                  key={org.id}
                  disabled={isDisabled}
                  className={styles.orgOption}
                  onClick={handleOrgSwitch(org.id)}
                >
                  <>
                    <TableProfile name={org.name} />
                    {isActived ? <ChipBadge color="primary" label="Active" /> : null}
                  </>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <DialogSessionToken
        onClose={onCloseSessionToken}
        open={showSessionTokenDialog}
        showImpersonatedUser={true}
      />
    </div>
  );
};

export default UserDropdown;
